import { FlaskConical, Microscope, Leaf, ClipboardCheck } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow, MaskedLines } from "../components/Reveal";
import { IMG } from "../data/products";

const WORKFLOW = [
  {
    step: "01",
    title: "Incoming raw material analysis",
    body: "Every raw-material lot is checked on arrival against its specification before it enters the plant.",
  },
  {
    step: "02",
    title: "In-process controls",
    body: "Granulation, particle size, moisture and flow behaviour are monitored through the production run.",
  },
  {
    step: "03",
    title: "Batch release testing",
    body: "Finished batches are analytically tested against the product specification before release for dispatch.",
  },
  {
    step: "04",
    title: "Stability & traceability",
    body: "Shelf-life behaviour and batch traceability are maintained so every consignment can be traced back.",
  },
];

const PILLARS = [
  {
    icon: FlaskConical,
    title: "Flavour & palatability science",
    body: "Sweetness curves, aroma profiles and their behaviour in feed — studied against intake behaviour, not just lab sheets.",
  },
  {
    icon: Microscope,
    title: "Delivery systems",
    body: "The Micropearl process and thermostable granulation protect potency through mixing, pelleting and storage.",
  },
  {
    icon: Leaf,
    title: "Botanical standardisation",
    body: "Herbal extracts such as curcumin and ashwagandha are standardised so every batch delivers the same profile.",
  },
  {
    icon: ClipboardCheck,
    title: "Application support",
    body: "Dosage guidance per species, feed type and process condition — from mineral mixture to complete pellet feed.",
  },
];

const Quality = () => (
  <div data-testid="page-quality">
    <Seo
      title="Quality & R&D"
      description="Quality control workflow and R&D disciplines behind Aurik feed additives — raw material analysis, in-process control, batch testing and formulation science."
    />

    <section className="grain mesh-glow relative overflow-hidden pb-20 pt-40 lg:pt-48">
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Eyebrow dark>Quality & R&D</Eyebrow>
        <MaskedLines
          as="h1"
          delay={0.2}
          className="mt-7 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          lines={["Quality is a process,", "not a claim."]}
        />
        <p className="mt-7 max-w-2xl text-sm leading-relaxed text-emerald-100/70 sm:text-base">
          From raw material to released batch, every Aurik product passes through a
          defined control workflow — and our R&D disciplines keep the range moving
          forward. Certifications and audit documents are available on request.
        </p>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <Reveal>
        <Eyebrow>Quality workflow</Eyebrow>
        <h2 className="mt-6 max-w-xl font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
          Four gates before a batch ships
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {WORKFLOW.map((w, i) => (
          <Reveal key={w.step} delay={i * 0.08} className="h-full">
            <div className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-colors hover:border-teal-600/40">
              <span className="font-display text-4xl font-extrabold text-emerald-100" aria-hidden="true">{w.step}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-night">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-y border-slate-200 bg-white py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div className="clip-frame overflow-hidden">
            <img src={IMG.lab} alt="Laboratory sample tubes during quality analysis" className="h-[380px] w-full object-cover lg:h-[480px]" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>R&D disciplines</Eyebrow>
            <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
              Where the next product comes from
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-8 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07}>
                <p.icon className="h-7 w-7 text-emerald-800" strokeWidth={1.6} />
                <h3 className="mt-4 font-display text-base font-bold text-night">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Quality;
